import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Button, Chip, Tab, Tabs } from '@nextui-org/react';
import type { AuditLogDetail, AuditLogDeltaItem, AuditLogMetadata } from '@circuitry-ai/doc-data';
import {
  getAuditLogSummaryMetrics,
  getAuditStatusChipClass,
  type AuditLogSummaryMetricKey,
} from './auditLogsUtils';

export type AuditLogDetailViewMode = 'reprocess' | 'data_change';
export type AuditLogJsonTabKey = 'metadata' | 'new_data' | 'old_data' | 'delta_data';

const DATA_CHANGE_AUDIT_TYPES = new Set(['update', 'create', 'delete']);

export function isDataChangeAuditType(auditType?: string): boolean {
  return DATA_CHANGE_AUDIT_TYPES.has(auditType?.toLowerCase() ?? '');
}

export function getAuditLogDetailViewMode(detail?: AuditLogDetail | null): AuditLogDetailViewMode {
  if (isDataChangeAuditType(detail?.audit_type)) return 'data_change';
  return 'reprocess';
}

export function normalizeAuditLogDeltaData(deltaData: AuditLogDetail['delta_data']): AuditLogDeltaItem[] {
  if (!Array.isArray(deltaData)) return [];
  return deltaData
    .map((item) => ({
      path: String(item?.path ?? ''),
      old: item?.old,
      new: item?.new,
    }))
    .filter((item) => item.path);
}

export function formatAuditLogDeltaValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function DetailFieldRow({
  label,
  value,
  children,
}: Readonly<{
  label: string;
  value?: React.ReactNode;
  children?: React.ReactNode;
}>) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-1 py-2 border-b border-default-100 last:border-b-0">
      <span className="text-sm text-default-500">{label}</span>
      <div className="min-w-0 text-sm font-medium break-words text-foreground">{children ?? value ?? '--'}</div>
    </div>
  );
}

export function MetricCard({
  label,
  value,
  valueClassName,
}: Readonly<{ label: string; value: React.ReactNode; valueClassName?: string }>) {
  const isPlaceholder = value === '—' || value === '-';
  let valueTextClass = 'text-foreground';
  if (isPlaceholder) {
    valueTextClass = 'text-default-400';
  } else if (valueClassName) {
    valueTextClass = valueClassName;
  }

  return (
    <div className="min-w-[120px] flex-1 rounded-lg border border-default-200 bg-default-50 px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-default-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${valueTextClass}`}>
        {value}
      </div>
    </div>
  );
}

interface AuditLogDetailFieldsProps {
  detail: AuditLogDetail;
  tenantDisplay: string;
  formattedWhen: string;
  moduleTitle: string;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}

export function AuditLogDetailFieldsSection({
  detail,
  tenantDisplay,
  formattedWhen,
  moduleTitle,
  copiedField,
  onCopy,
}: Readonly<AuditLogDetailFieldsProps>) {
  const { t } = useTranslation();
  const requestId = detail.metadata?.request_id;

  return (
    <section>
      <DetailFieldRow label={t('Audit ID')}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs break-all">{detail.audit_id}</span>
          <Button
            size="sm"
            variant="bordered"
            className="h-7 shrink-0 px-2"
            onPress={() => onCopy(detail.audit_id, 'audit_id')}
          >
            {copiedField === 'audit_id' ? t('Copied') : t('Copy')}
          </Button>
        </div>
      </DetailFieldRow>
      <DetailFieldRow label={t('Tenant')} value={tenantDisplay} />
      <DetailFieldRow label={t('Date')} value={formattedWhen} />
      <DetailFieldRow
        label={t('Actor')}
        value={[detail.executed_by, detail.user_id].filter(Boolean).join(' · ')}
      />
      <DetailFieldRow label={t('Module')} value={moduleTitle} />
      <DetailFieldRow label={t('Resource ID')} value={detail.resource_id || '--'} />
      {requestId ? <DetailFieldRow label={t('Request ID')} value={requestId} /> : null}
    </section>
  );
}

interface AuditLogFieldLevelDiffProps {
  deltaRows: AuditLogDeltaItem[];
}

export function AuditLogFieldLevelDiffSection({ deltaRows }: Readonly<AuditLogFieldLevelDiffProps>) {
  const { t } = useTranslation();

  return (
    <section>
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-default-500">
        {t('Field-level diff ({{count}})', { count: deltaRows.length })}
      </h3>
      <div className="overflow-x-auto rounded-lg border border-default-200">
        <table className="w-full min-w-max text-sm">
          <thead className="bg-default-100 text-left text-xs uppercase text-default-500">
            <tr>
              <th className="px-3 py-2">{t('Path')}</th>
              <th className="px-3 py-2">{t('Old')}</th>
              <th className="px-3 py-2">{t('New')}</th>
            </tr>
          </thead>
          <tbody>
            {deltaRows.map((row) => (
              <tr key={row.path} className="border-t border-default-100 align-top">
                <td className="px-3 py-2 font-mono text-xs">{row.path}</td>
                <td className="bg-danger/10 px-3 py-2 font-mono text-xs whitespace-pre-wrap break-words">
                  {formatAuditLogDeltaValue(row.old)}
                </td>
                <td className="bg-success/10 px-3 py-2 font-mono text-xs whitespace-pre-wrap break-words">
                  {formatAuditLogDeltaValue(row.new)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface AuditLogFullSnapshotsProps {
  oldData: Record<string, unknown> | null | undefined;
  newData: Record<string, unknown> | null | undefined;
}

export function AuditLogFullSnapshotsSection({ oldData, newData }: Readonly<AuditLogFullSnapshotsProps>) {
  const { t } = useTranslation();
  const hasOld = oldData != null && Object.keys(oldData).length > 0;
  const hasNew = newData != null && Object.keys(newData).length > 0;
  if (hasOld || hasNew) {
    return (
      <section>
        <Accordion
          variant="light"
          className="px-0"
          itemClasses={{
            base: 'px-0 shadow-none bg-transparent border-none',
            heading: 'px-0',
            trigger: 'py-1 px-0 gap-2',
            title: 'text-sm font-bold text-foreground',
            indicator: 'text-foreground',
            content: 'pt-2 pb-0 px-0',
          }}
        >
          <AccordionItem
            key="full_snapshots"
            aria-label={t('Full snapshots')}
            title={t('Full snapshots (old_data / new_data)')}
          >
            <div className="space-y-3">
              {hasOld ? (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase text-default-500">{t('old_data')}</p>
                  <pre className="max-h-48 overflow-auto rounded-lg border border-default-200 bg-default-50 p-3 text-xs font-mono">
                    {JSON.stringify(oldData, null, 2)}
                  </pre>
                </div>
              ) : null}
              {hasNew ? (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase text-default-500">{t('new_data')}</p>
                  <pre className="max-h-48 overflow-auto rounded-lg border border-default-200 bg-default-50 p-3 text-xs font-mono">
                    {JSON.stringify(newData, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          </AccordionItem>
        </Accordion>
      </section>
    );
  }
  return null;
}

export function AuditLogRawJsonSection({ detail }: Readonly<{ detail: AuditLogDetail }>) {
  const { t } = useTranslation();
  const [activeJsonTab, setActiveJsonTab] = useState<AuditLogJsonTabKey>('metadata');

  const jsonTabData = useMemo(
    () => ({
      metadata: detail.metadata ?? null,
      new_data: detail.new_data ?? null,
      old_data: detail.old_data ?? null,
      delta_data: detail.delta_data ?? null,
    }),
    [detail]
  );

  return (
    <section>
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-default-500">
        {t('Raw JSONB columns')}
      </h3>
      <Tabs
        selectedKey={activeJsonTab}
        onSelectionChange={(key) => setActiveJsonTab(key as AuditLogJsonTabKey)}
        variant="underlined"
        classNames={{ tabList: 'gap-4' }}
      >
        {(['metadata', 'new_data', 'old_data', 'delta_data'] as const).map((tabKey) => (
          <Tab key={tabKey} title={tabKey} />
        ))}
      </Tabs>
      <pre className="mt-3 max-h-80 overflow-auto rounded-lg border border-primary bg-primary/10 p-4 text-xs font-mono text-default-800">
        {jsonTabData[activeJsonTab] != null ? JSON.stringify(jsonTabData[activeJsonTab], null, 2) : 'null'}
      </pre>
    </section>
  );
}

function buildReprocessMetadataFields(metadata: AuditLogMetadata | null | undefined, t: (key: string) => string) {
  if (!metadata) return [];
  const fields: Array<{ label: string; value: string }> = [];
  if (metadata.mode) fields.push({ label: t('Mode'), value: String(metadata.mode) });
  if (metadata.aiworker_name || metadata.aiworker_id) {
    fields.push({
      label: t('AI Worker'),
      value: [metadata.aiworker_name, metadata.aiworker_id].filter(Boolean).join(' · '),
    });
  }
  if (metadata.target_id) fields.push({ label: t('Target (credential)'), value: String(metadata.target_id) });
  if (metadata.claim_column) fields.push({ label: t('Claim column'), value: String(metadata.claim_column) });
  if (metadata.reason) fields.push({ label: t('Reason'), value: String(metadata.reason) });
  if (typeof metadata.duration_ms === 'number') {
    fields.push({ label: t('Duration'), value: `${metadata.duration_ms} ms` });
  }
  return fields;
}

interface AuditLogReprocessBodyProps {
  detail: AuditLogDetail;
}

export function AuditLogReprocessBody({ detail }: Readonly<AuditLogReprocessBodyProps>) {
  const { t } = useTranslation();
  const metadata = detail.metadata;

  const summaryMetrics = useMemo(() => {
    const values = getAuditLogSummaryMetrics(detail);
    const metricConfig: Array<{
      key: AuditLogSummaryMetricKey;
      label: string;
      valueClassName?: (count: number | undefined) => string | undefined;
    }> = [
      { key: 'row_count', label: t('ROWS') },
      { key: 'distinct_claim_count', label: t('DISTINCT CLAIMS') },
      {
        key: 'successful_count',
        label: t('ENQUEUED OK'),
        valueClassName: (count) => (count !== undefined ? 'text-success' : undefined),
      },
      {
        key: 'failed_count',
        label: t('FAILED'),
        valueClassName: (count) => (count !== undefined && count > 0 ? 'text-danger' : undefined),
      },
    ];

    return metricConfig.map((config) => {
      const count = values[config.key];
      return {
        key: config.key,
        label: config.label,
        value: count !== undefined ? count : '—',
        className: config.valueClassName?.(count),
      };
    });
  }, [detail, t]);

  const metadataDetailFields = useMemo(() => buildReprocessMetadataFields(metadata, t), [metadata, t]);

  const claimNumbers = useMemo(() => {
    if (!Array.isArray(metadata?.claim_numbers)) return [];
    return metadata.claim_numbers.map((item) => String(item).trim()).filter(Boolean);
  }, [metadata?.claim_numbers]);

  const messageIds = useMemo(() => {
    if (!Array.isArray(metadata?.message_ids)) return [];
    return metadata.message_ids.filter((item) => item?.user_input || item?.message_id);
  }, [metadata?.message_ids]);

  const failedItems = metadata?.failed_items ?? [];

  return (
    <>
      <section>
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-default-500">
          {t('Reprocess summary')}
        </h3>
        <div className="flex flex-wrap gap-3">
          {summaryMetrics.map((metric) => (
            <MetricCard
              key={metric.key}
              label={metric.label}
              value={metric.value}
              valueClassName={metric.className}
            />
          ))}
        </div>
      </section>

      {metadataDetailFields.length > 0 ? (
        <section>
          {metadataDetailFields.map((field) => (
            <DetailFieldRow key={field.label} label={field.label} value={field.value} />
          ))}
        </section>
      ) : null}

      {metadata?.sql_query ? (
        <section>
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-default-500">
            {t('SQL query')}
          </h3>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-default-200 bg-default-50 p-4 font-mono text-xs text-default-700">
            {metadata.sql_query}
          </pre>
        </section>
      ) : null}

      {(claimNumbers.length > 0 || messageIds.length > 0) && (
        <section className="space-y-2">
          {claimNumbers.length > 0 ? (
            <Accordion
              variant="light"
              selectionMode="multiple"
              defaultExpandedKeys={['claim_numbers']}
              className="px-0"
              itemClasses={{
                base: 'px-0 shadow-none border-none bg-transparent',
                heading: 'px-0',
                trigger: 'gap-2 px-0 py-1',
                title: 'text-sm font-bold text-foreground',
                indicator: 'text-foreground',
                content: 'px-0 pb-0 pt-2',
              }}
            >
              <AccordionItem
                key="claim_numbers"
                title={t('Claim numbers ({{count}})', { count: claimNumbers.length })}
              >
                <div className="max-h-44 overflow-auto rounded-md border border-default-200 bg-default-100 p-3">
                  <p className="break-words font-mono text-xs leading-relaxed text-default-700">
                    {claimNumbers.join(', ')}
                  </p>
                </div>
              </AccordionItem>
            </Accordion>
          ) : null}
          {messageIds.length > 0 ? (
            <Accordion
              variant="light"
              selectionMode="multiple"
              defaultExpandedKeys={['message_ids']}
              className="px-0"
              itemClasses={{
                base: 'px-0 shadow-none border-none bg-transparent',
                heading: 'px-0',
                trigger: 'gap-2 px-0 py-1',
                title: 'text-sm font-bold text-foreground',
                indicator: 'text-foreground',
                content: 'px-0 pb-0 pt-2',
              }}
            >
              <AccordionItem
                key="message_ids"
                title={t('SQS message IDs ({{count}})', { count: messageIds.length })}
              >
                <div className="max-h-44 overflow-auto rounded-md border border-default-200 bg-default-100 p-3">
                  <p className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-default-700">
                    {messageIds
                      .map((item) => [item.user_input, item.message_id].filter(Boolean).join(' · '))
                      .join('\n')}
                  </p>
                </div>
              </AccordionItem>
            </Accordion>
          ) : null}
        </section>
      )}

      {failedItems.length > 0 ? (
        <section>
          <h3 className="mb-2 text-sm font-semibold">
            {t('Failed items ({{count}})', { count: failedItems.length })}
          </h3>
          <div className="overflow-hidden rounded-lg border border-default-200">
            <table className="w-full text-sm">
              <thead className="bg-default-100 text-left text-xs uppercase text-default-500">
                <tr>
                  <th className="px-3 py-2">{t('Claim')}</th>
                  <th className="px-3 py-2">{t('Error')}</th>
                </tr>
              </thead>
              <tbody>
                {failedItems.map((item, index) => (
                  <tr key={`${item.user_input}-${index}`} className="border-t border-default-100">
                    <td className="px-3 py-2 font-mono text-xs">{item.user_input ?? '--'}</td>
                    <td className="px-3 py-2 text-danger">{item.error ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}

interface AuditLogDataChangeBodyProps {
  detail: AuditLogDetail;
}

export function AuditLogDataChangeBody({ detail }: AuditLogDataChangeBodyProps) {
  const deltaRows = useMemo(() => normalizeAuditLogDeltaData(detail.delta_data), [detail.delta_data]);

  return (
    <>
      {deltaRows.length > 0 ? <AuditLogFieldLevelDiffSection deltaRows={deltaRows} /> : null}
      <AuditLogFullSnapshotsSection oldData={detail.old_data} newData={detail.new_data} />
    </>
  );
}

interface AuditLogDetailBodyProps {
  detail: AuditLogDetail;
  tenantDisplay: string;
  formattedWhen: string;
  moduleTitle: string;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}

export function AuditLogDetailBody({
  detail,
  tenantDisplay,
  formattedWhen,
  moduleTitle,
  copiedField,
  onCopy,
}: Readonly<AuditLogDetailBodyProps>) {
  const viewMode = getAuditLogDetailViewMode(detail);

  return (
    <div className="space-y-6">
      <AuditLogDetailFieldsSection
        detail={detail}
        tenantDisplay={tenantDisplay}
        formattedWhen={formattedWhen}
        moduleTitle={moduleTitle}
        copiedField={copiedField}
        onCopy={onCopy}
      />
      {viewMode === 'data_change' ? (
        <AuditLogDataChangeBody detail={detail} />
      ) : (
        <AuditLogReprocessBody detail={detail} />
      )}
      <AuditLogRawJsonSection detail={detail} />
    </div>
  );
}